// src/components/common/ConfirmationPopup.jsx
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"; // Using outline icons for warning/info
import { AiOutlineLoading3Quarters } from "react-icons/ai"; // Loading icon

const ConfirmationPopup = ({
  isOpen,
  onClose, // Handles cancel/overlay click
  onConfirm, // Function to call when confirmed
  message,
  title = "Confirm Action",
  confirmText = "Confirm",
  cancelText = "Cancel",
  isLoading = false, // To show loading state on confirm button
}) => {
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-60 p-4" // Increased z-index
          onClick={handleOverlayClick}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 max-w-sm w-full relative text-center"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              disabled={isLoading}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              aria-label="Close popup"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>

            {/* Icon */}
            <div className="flex justify-center mb-4">
              {/* Using a warning icon for confirmation */}
              <ExclamationTriangleIcon className="w-14 h-14 text-orange-500" />
            </div>

            {/* Title */}
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {title}
            </h3>
            {/* Message */}
            <p className="text-sm text-gray-600 mb-6">{message}</p>

            {/* Action Buttons */}
            <div className="flex justify-center gap-4">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium text-sm disabled:opacity-60"
              >
                {cancelText}
              </button>
              <button
                onClick={onConfirm}
                disabled={isLoading}
                className={`px-5 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium text-sm flex items-center justify-center min-w-[100px] disabled:opacity-60 ${
                  isLoading ? "cursor-wait" : ""
                }`}
              >
                {isLoading ? (
                  <AiOutlineLoading3Quarters className="animate-spin h-4 w-4" />
                ) : (
                  confirmText
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConfirmationPopup;
